import type { DesignTokens, ThemeRow, ThemeTokens } from "../types/theme";
import { DARK_BASE_TOKENS, LIGHT_BASE_TOKENS } from "../constants/theme-tokens";
import type { TypographyContextValue } from "../types/typography";
import { getSafeFamilyName } from "@fleet/typography";
import { PAIR_TOKEN_MAP, TOKEN_MAP } from "./token-maps";

/**
 * Resolver for theme tokens, including atmospheric "Pair" logic.
 * Encapsulates the mapping from Database rows to the ThemeTokens UI representation.
 */

const GLOW_INDEX_0 = 0;
const GLOW_INDEX_1 = 1;
const GLOW_INDEX_2 = 2;

function isStringArray(arr: unknown): arr is string[] {
  return Array.isArray(arr) && arr.every((item) => typeof item === "string");
}

function getVariant(
  map: Record<string, string | undefined>,
  keys: string[],
  fallback: string,
): string {
  for (const key of keys) {
    const val = map[key];
    if (val) {
      return val;
    }
  }
  return fallback;
}

function resolveFontFromMap(
  family: string,
  map: Record<string, string | undefined>,
): { fontNormal: string; fontMedium: string; fontSemiBold: string; fontBold: string } {
  const safeFamily = family.replaceAll(/\s+/gu, "");
  const safeFallback = `${safeFamily}-Regular`;

  return {
    fontNormal: getVariant(map, ["-Regular"], safeFallback),
    fontMedium: getVariant(map, ["-Medium", "-Regular"], safeFallback),
    fontSemiBold: getVariant(map, ["-SemiBold", "-Bold", "-Regular"], safeFallback),
    fontBold: getVariant(map, ["-Bold", "-Regular"], safeFallback),
  };
}

/**
 * Resolves font-variant keys from the user's typography preference.
 */
export function resolveFontVariants(
  userFonts: TypographyContextValue | undefined,
  fallbacks: { fontNormal: string; fontMedium: string; fontSemiBold: string; fontBold: string },
): { fontNormal: string; fontMedium: string; fontSemiBold: string; fontBold: string } {
  const family = getSafeFamilyName({
    family: userFonts?.fontFamilyName,
    id: userFonts?.fontFamily ?? undefined,
  });

  if (!family) {
    return fallbacks;
  }

  const map = userFonts?.variantMap;
  if (!map || Object.keys(map).length === 0) {
    const safeFamily = family.replaceAll(/\s+/gu, "");
    const safeFallback = `${safeFamily}-Regular`;
    return {
      fontNormal: safeFallback,
      fontMedium: safeFallback,
      fontSemiBold: safeFallback,
      fontBold: safeFallback,
    };
  }

  return resolveFontFromMap(family, map);
}

function parseGlowColors(rawGlow: string | null | undefined): string[] | null {
  if (!rawGlow) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(rawGlow);
    if (isStringArray(parsed) && parsed.length > 0) {
      return parsed;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function resolveIsDark(themeResult: ThemeRow, systemIsDark: boolean): boolean {
  const shouldFollowDeviceTheme = Boolean(themeResult.follow_device_theme);
  return shouldFollowDeviceTheme ? systemIsDark : themeResult.is_dark === 1;
}

function shouldUsePair(themeResult: ThemeRow, isDark: boolean): boolean {
  const shouldFollowDeviceTheme = Boolean(themeResult.follow_device_theme);
  const targetIsDark = isDark ? 1 : 0;

  return Boolean(
    themeResult.p_id &&
    shouldFollowDeviceTheme &&
    themeResult.is_dark !== targetIsDark &&
    themeResult.p_is_dark === targetIsDark,
  );
}

function getFallback<K extends keyof DesignTokens>(
  token: K,
  baseTokens: DesignTokens,
  parentTokens: ThemeTokens | null,
): DesignTokens[K] {
  const parentVal = parentTokens?.[token];
  if (parentVal !== undefined && parentVal !== "") {
    return parentVal as DesignTokens[K];
  }

  const bVal = baseTokens[token];
  if (bVal !== undefined && bVal !== "") {
    return bVal;
  }

  return bVal;
}

function isValidTokenValue<K extends keyof DesignTokens>(
  token: K,
  value: unknown,
): value is DesignTokens[K] {
  if (token === "patternOpacity") {
    return typeof value === "number";
  }
  return typeof value === "string" || typeof value === "number";
}

function createGetRowVal(
  themeResult: ThemeRow,
  baseTokens: DesignTokens,
  parentTokens: ThemeTokens | null,
  usePair: boolean,
) {
  return function getRowVal<K extends keyof DesignTokens>(token: K): DesignTokens[K] {
    const finalKey = usePair ? PAIR_TOKEN_MAP[token] : TOKEN_MAP[token];
    if (!finalKey) {
      return getFallback(token, baseTokens, parentTokens);
    }

    const value = themeResult[finalKey];
    if (value === null || value === "") {
      return getFallback(token, baseTokens, parentTokens);
    }

    const result = token === "patternOpacity" ? Number(value) : value;

    if (isValidTokenValue(token, result)) {
      return result;
    }

    return getFallback(token, baseTokens, parentTokens);
  };
}

function buildAccentToken(
  getRowVal: (token: keyof DesignTokens) => DesignTokens[keyof DesignTokens],
  themeResult: ThemeRow,
  parentTokens: ThemeTokens | null,
  baseTokens: DesignTokens,
): string {
  const accent = getRowVal("accent");
  if (typeof accent === "string" && accent !== "") {
    return accent;
  }
  if (themeResult.list_color) {
    return themeResult.list_color;
  }
  if (parentTokens?.accent && parentTokens.accent !== "") {
    return parentTokens.accent;
  }
  return baseTokens.accent;
}

function buildCoreTokens(
  getRowVal: (token: keyof DesignTokens) => DesignTokens[keyof DesignTokens],
) {
  return {
    background: String(getRowVal("background")),
    foreground: String(getRowVal("foreground")),
    surface: String(getRowVal("surface")),
    surfaceForeground: String(getRowVal("surfaceForeground")),
    surfaceSecondary: String(getRowVal("surfaceSecondary")),
    surfaceSecondaryForeground: String(getRowVal("surfaceSecondaryForeground")),
    surfaceTertiary: String(getRowVal("surfaceTertiary")),
    surfaceTertiaryForeground: String(getRowVal("surfaceTertiaryForeground")),
    overlay: String(getRowVal("overlay")),
    overlayForeground: String(getRowVal("overlayForeground")),
  };
}

function buildSemanticTokens(
  getRowVal: (token: keyof DesignTokens) => DesignTokens[keyof DesignTokens],
) {
  return {
    muted: String(getRowVal("muted")),
    default: String(getRowVal("default")),
    defaultForeground: String(getRowVal("defaultForeground")),
    success: String(getRowVal("success")),
    successForeground: String(getRowVal("successForeground")),
    warning: String(getRowVal("warning")),
    warningForeground: String(getRowVal("warningForeground")),
    danger: String(getRowVal("danger")),
    dangerForeground: String(getRowVal("dangerForeground")),
  };
}

function buildLayoutTokens(
  getRowVal: (token: keyof DesignTokens) => DesignTokens[keyof DesignTokens],
) {
  return {
    segment: String(getRowVal("segment")),
    segmentForeground: String(getRowVal("segmentForeground")),
    separator: String(getRowVal("separator")),
    border: String(getRowVal("border")),
    focus: String(getRowVal("focus")),
    link: String(getRowVal("link")),
    radius: String(getRowVal("radius")),
  };
}

function buildGlassTokens(
  getRowVal: (token: keyof DesignTokens) => DesignTokens[keyof DesignTokens],
  baseTokens: DesignTokens,
  glowArray: string[] | null,
  accent: string,
) {
  return {
    glassBackground: String(getRowVal("glassBackground")),
    glassBorder: String(getRowVal("glassBorder")),
    glassHighlight: String(getRowVal("glassHighlight")),
    glow0: (glowArray ? glowArray[GLOW_INDEX_0] : null) ?? accent,
    glow1: (glowArray ? glowArray[GLOW_INDEX_1] : null) ?? baseTokens.glow1,
    glow2: (glowArray ? glowArray[GLOW_INDEX_2] : null) ?? baseTokens.glow2,
  };
}

/**
 * Resolves raw database rows into final ThemeTokens for the UI.
 */
export function resolveThemeTokens({
  themeResult,
  isBlurContext,
  systemIsDark,
  parentTokens,
  userFonts,
}: {
  themeResult: ThemeRow;
  isBlurContext: boolean;
  systemIsDark: boolean;
  parentTokens: ThemeTokens | null;
  userFonts?: TypographyContextValue;
}): ThemeTokens {
  const isDark = resolveIsDark(themeResult, systemIsDark);
  const usePair = shouldUsePair(themeResult, isDark);
  const baseTokens = isDark ? DARK_BASE_TOKENS : LIGHT_BASE_TOKENS;
  const glowArray = parseGlowColors(usePair ? themeResult.p_glow_colors : themeResult.glow_colors);
  const getRowVal = createGetRowVal(themeResult, baseTokens, parentTokens, usePair);

  const isBlur = resolveIsBlur(themeResult, isBlurContext);

  const accent = buildAccentToken(getRowVal, themeResult, parentTokens, baseTokens);
  const coreTokens = buildCoreTokens(getRowVal);
  const semanticTokens = buildSemanticTokens(getRowVal);
  const layoutTokens = buildLayoutTokens(getRowVal);
  const glassTokens = buildGlassTokens(getRowVal, baseTokens, glowArray, accent);

  return buildThemeTokens({
    themeResult,
    usePair,
    isDark,
    isBlur,
    accent,
    coreTokens,
    semanticTokens,
    layoutTokens,
    glassTokens,
    getRowVal,
    userFonts,
  });
}

function resolveIsBlur(themeResult: ThemeRow, isBlurContext: boolean): boolean {
  if (themeResult.is_blur === 1) {
    return true;
  }
  if (themeResult.is_blur === 0) {
    return false;
  }
  return isBlurContext;
}

interface BuildThemeTokensParams {
  themeResult: ThemeRow;
  usePair: boolean;
  isDark: boolean;
  isBlur: boolean;
  accent: string;
  coreTokens: ReturnType<typeof buildCoreTokens>;
  semanticTokens: ReturnType<typeof buildSemanticTokens>;
  layoutTokens: ReturnType<typeof buildLayoutTokens>;
  glassTokens: ReturnType<typeof buildGlassTokens>;
  getRowVal: (token: keyof DesignTokens) => DesignTokens[keyof DesignTokens];
  userFonts?: TypographyContextValue;
}

function buildFieldTokens(
  getRowVal: (token: keyof DesignTokens) => DesignTokens[keyof DesignTokens],
) {
  return {
    fieldBackground: String(getRowVal("fieldBackground")),
    fieldForeground: String(getRowVal("fieldForeground")),
    fieldPlaceholder: String(getRowVal("fieldPlaceholder")),
    fieldBorder: String(getRowVal("fieldBorder")),
  };
}

function buildShadowTokens(
  getRowVal: (token: keyof DesignTokens) => DesignTokens[keyof DesignTokens],
) {
  return {
    surfaceShadow: String(getRowVal("surfaceShadow")),
    overlayShadow: String(getRowVal("overlayShadow")),
    fieldShadow: String(getRowVal("fieldShadow")),
  };
}

function validatePatternType(
  value: DesignTokens[keyof DesignTokens] | undefined,
): "none" | "dots" | "grid" | "noise" {
  const str = String(value ?? "none");
  if (str === "dots" || str === "grid" || str === "noise") {
    return str;
  }
  return "none";
}

function buildRemainingTokens(
  getRowVal: (token: keyof DesignTokens) => DesignTokens[keyof DesignTokens],
  accent: string,
) {
  return {
    patternType: validatePatternType(getRowVal("patternType")),
    patternOpacity: Number(getRowVal("patternOpacity")),
    backdrop: String(getRowVal("backdrop")),
    mutedForeground: String(getRowVal("mutedForeground")),
    primary: accent,
    primaryForeground: String(getRowVal("accentForeground")),
    secondary: String(getRowVal("default")),
    secondaryForeground: String(getRowVal("defaultForeground")),
  };
}

function buildThemeTokens({
  themeResult,
  usePair,
  isDark,
  isBlur,
  accent,
  coreTokens,
  semanticTokens,
  layoutTokens,
  glassTokens,
  getRowVal,
  userFonts,
}: BuildThemeTokensParams): ThemeTokens {
  return {
    id: (usePair ? themeResult.p_id : themeResult.id) ?? "none",
    isDark,
    isBlur,
    ...coreTokens,
    ...semanticTokens,
    accent,
    accentForeground: String(getRowVal("accentForeground")),
    ...layoutTokens,
    ...resolveFontVariants(userFonts, {
      fontNormal: String(getRowVal("fontNormal")),
      fontMedium: String(getRowVal("fontMedium")),
      fontSemiBold: String(getRowVal("fontSemiBold")),
      fontBold: String(getRowVal("fontBold")),
    }),
    ...buildFieldTokens(getRowVal),
    ...buildShadowTokens(getRowVal),
    ...glassTokens,
    ...buildRemainingTokens(getRowVal, accent),
  };
}
const FULL_RADIUS = 9999;
const RADIUS_MULTIPLIERS: Record<string, number> = {
  xs: 0.25,
  sm: 0.5,
  md: 0.75,
  lg: 1,
  xl: 1.5,
  "2xl": 2,
  "3xl": 3,
  "4xl": 4,
};

/**
 * Calculates a derived radius based on a base value and scale.
 * Mirrors the multiplier logic used in the design system.
 */
export function calculateDerivedRadius(baseRadiusPx: number, scale: string): number {
  if (scale === "full") {
    return FULL_RADIUS;
  }

  const multiplier = RADIUS_MULTIPLIERS[scale] ?? 1;
  return Math.round(baseRadiusPx * multiplier);
}

/**
 * Provides default glass colors when none are defined in the theme.
 */
export function resolveDefaultGlassColors(isDark: boolean): {
  background: string;
  border: string;
  highlight: string;
} {
  return {
    background: isDark ? "rgba(10, 10, 10, 0.985)" : "rgba(255, 255, 255, 0.85)",
    border: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.1)",
    highlight: isDark ? "rgba(255, 255, 255, 0.25)" : "rgba(0, 0, 0, 0.08)",
  };
}
